import Popup from "reactjs-popup";
import { clickable } from "../../utils/a11y";
import React, { useEffect, useState } from "react";
import moment from "moment";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea, } from 'recharts';
import "../../styles/Patient-ui/Patient-symptom-chart.css";
import { SEVERITY_TIERS, getSeverityTier, getScoreColor, getSeverityLabel, hasSymptomScore } from '../../utils/symptomSeverity';
import { isWithinAssessmentWindow, hasAssessmentForAppt } from '../../utils/symptomAssessment';
//import { API_DETAILS } from '../../App';

const PatientSymptomChart = (props) => {
  const [showRecentScoreView, setShowRecentScoreView] = useState(true);
  const [isFilling, setIsFilling] = useState(false);
  const [showSeverity, setShowSeverity] = useState(false);
  const [activeTab, setActiveTab] = useState('score');
  let usrId = props.uId;

  const toggleView = () => {
    setShowRecentScoreView(!showRecentScoreView);
    if (showRecentScoreView) {
      props.setScore(0);
    }
  };

  // The score counts up from 0 to the latest assessment. targetScore is derived
  // rather than read inside the interval so it can be a dependency: the effect
  // previously did not list selectedScore, so when the scores finished loading it
  // never re-ran and the count-up never started. The card sat at 0 until something
  // else — changing the diagnosis — happened to re-run it, which is why the score
  // only appeared after clicking the dropdown a few times.
  const targetScore =
    props.selectedScore.length > 0
      ? props.selectedScore[props.selectedScore.length - 1]['symptomScore']
      : 0;

  useEffect(() => {
    let interval;
    if (targetScore > 0 && showRecentScoreView && props.score < targetScore) {
      // The count-up is decoration. Someone who has asked their system for less
      // motion gets the final number straight away (DESIGN_SYSTEM.md §7).
      const reduceMotion =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduceMotion) {
        props.setScore(targetScore);
        return undefined;
      }
      setIsFilling(true);
      interval = setInterval(() => {
        props.setScore(prevScore =>
          prevScore < targetScore ? prevScore + 1 : prevScore
        );
      }, 20);
    }
    return () => clearInterval(interval);
  }, [showRecentScoreView, props.score, props.selectedOption, targetScore]);

  const circleRadius = 50;
  const circleCircumference = 2 * Math.PI * circleRadius;

  // TODO: Assuming already sorted. Cross-check!
  const matchingAppts = props.activeAppts
    ?.filter(appt => Number(appt.diagId) === Number(props.selectedOption)) || []

  // Eligibility now comes from one shared rule, so this button and the
  // appointment cards on the home page cannot disagree about the same visit.
  const nextEligibleAppt = matchingAppts.find(isWithinAssessmentWindow);
  const alreadyAssessedForAppt = !!nextEligibleAppt && hasAssessmentForAppt(nextEligibleAppt);
  const canTakeAssessment = !!nextEligibleAppt && !alreadyAssessedForAppt;

  return (
    <div>
      {
        <>
          {/*<div style={{ width: "100%", height: '35vh', alignItems: 'end', position: 'relative' }} className = "prev-symp-score-1">
          </div>*/}
          <div style={{ backgroundColor: '#ffffff', minHeight: '38vh', display: 'flex', flexDirection: 'column', gap: '0.7rem', border: '1px solid #e5e7eb', borderRadius: '10px', position: 'relative', overflow: 'hidden', padding: '24px 24px 10px 24px' }}>

            {/* Header Section */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', lineHeight: '1' }}>
                  {activeTab === 'score' ? 'Latest Symptom Score' : 'Score Trend'}
                </h4>
                <p style={{ margin: '-1px 0 0 0', fontSize: '0.875rem', opacity: '0.7' }}>
                  {props.selectedScore && (
                    activeTab === 'score'
                      ? (props.selectedScore[props.selectedScore.length - 1]?.dateTime && hasSymptomScore(props.score)
                          ? `Taken on ${new Date(props.selectedScore[props.selectedScore.length - 1]?.dateTime).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                          : '\u00A0')
                      : 'Last few assessments'
                  )}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setActiveTab('score')}
                  style={{
                    backgroundColor: activeTab === 'score' ? 'var(--youro-sage)' : 'transparent',
                    color: activeTab === 'score' ? '#ffffff' : 'var(--youro-sage)',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Score
                </button>
                <button
                  onClick={() => setActiveTab('trend')}
                  style={{
                    backgroundColor: activeTab === 'trend' ? 'var(--youro-sage)' : 'transparent',
                    color: activeTab === 'trend' ? '#ffffff' : 'var(--youro-sage)',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Trend
                </button>
              </div>
            </div>

            {/* Conditional Content Section */}
            <div style={{ minHeight: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {activeTab === 'score' ? (
                <div style={{ width: '100%' }}>
                  {/* Score View */}
                  {((props.data && props.data.length == 0) || (!props.selectedOption && showRecentScoreView) || !hasSymptomScore(props.score)) ? (
                      /* No Record State — also covers a score of 0, which this
                         codebase uses to mean "no assessment recorded" rather
                         than a genuine worst-case result. */
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="prev-symp-helper">
                        <h3 style={{ display: 'inline-block', fontStyle: 'italic', padding: '3%', margin: 0, fontSize: '0.95rem' }}>
                          <i>No Record of Symptom Score</ i>
                        </h3>
                      </div>
                    ) : (
                      /* Score View */
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0' }}>
                          <div style={{ width: '70%' }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', paddingTop: '16px', paddingBottom: '2px' }}>
                              <span style={{ fontSize: '3.5rem', fontWeight: '900', color: getScoreColor(props.score), WebkitTextStroke: `2px ${getScoreColor(props.score)}` }}>
                                {props.score}
                              </span>
                              <span style={{ fontSize: '1.25rem', opacity: '0.7' }}>out of 100</span>
                            </div>
                            <p
                              style={{ margin: '2px 0 0 0', fontSize: '1.125rem', fontWeight: '500', color: getScoreColor(props.score), paddingBottom: '16px', cursor: 'pointer', textDecoration: 'underline', width: 'fit-content' }}
                              {...clickable(() => setShowSeverity(true), `${getSeverityLabel(props.score)}: what this means`)}
                            >
                              {getSeverityLabel(props.score)}
                            </p>
                            <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.3', opacity: '0.7' }}>
                              {getSeverityTier(props.score).guidance}
                            </p>
                          </div>
                          <div style={{ width: '30%', position: 'relative', right: '-10px', display: 'flex', justifyContent: 'center' }}>
                            <svg width="180" height="180" viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
                              <circle cx="60" cy="60" r="50" fill="none" stroke={getSeverityTier(props.score).bgColor} strokeWidth="8" />
                              <circle
                                cx="60" cy="60" r="50" fill="none" stroke={getScoreColor(props.score)} strokeWidth="8"
                                strokeLinecap="round"
                                strokeDasharray={`${(props.score / 100) * circleCircumference} ${circleCircumference}`}
                              />
                            </svg>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
              ) : (
                /* Trend View */
                <div style={{ width: '100%', height: '190px', display: 'flex', flexDirection: 'column' }}>
                  {
                      props.selectedOption != '' && props.selectedScore.length > 0 && <>
                        {/* Severity bands make the line readable at a glance: without them
                            a number on a 0-100 axis says nothing about how the patient is
                            actually doing, and a rising line reads as "getting worse" when
                            on this scale a higher score means milder symptoms. */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px', flexShrink: 0 }}>
                          <span style={{ fontSize: '0.75rem', color: 'var(--youro-muted)' }}>
                            Higher is better — bands show how bothersome symptoms are
                          </span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--youro-muted)' }}>
                            {props.selectedScore.length} assessment{props.selectedScore.length === 1 ? '' : 's'}
                          </span>
                        </div>
                        {/* ResponsiveContainer only treats a string height as relative when
                            it ends in "%" — a calc() expression is not recognised and the
                            chart silently renders at zero height. Give it a flex parent with
                            a real height instead and keep the container at 100%. */}
                        <div style={{ flex: 1, minHeight: 0 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={props.selectedScore}
                            margin={{
                              top: 8,
                              right: 16,
                              left: -18,
                              bottom: 0,
                            }}
                          >
                            {/* One band per tier, drawn from the same source the score
                                colours come from so the two can never drift apart. */}
                            {SEVERITY_TIERS.map((tier) => (
                              <ReferenceArea
                                key={tier.label}
                                y1={tier.min - 1}
                                y2={tier.max}
                                fill={tier.bgColor}
                                fillOpacity={0.55}
                                strokeOpacity={0}
                                ifOverflow="hidden"
                              />
                            ))}
                            <CartesianGrid horizontal vertical={false} stroke="var(--youro-border)" />
                            <XAxis
                              dataKey='unix'
                              type='number'
                              stroke="var(--youro-muted)"
                              tick={{ fontSize: 12 }}
                              tickLine={false}
                              domain={['dataMin', 'dataMax']}
                              tickFormatter={unix => {
                                const dt = new Date(unix);
                                return moment(dt).format('MMM D');
                              }}
                            />
                            {/* Ticks sit on the tier boundaries, so every gridline means
                                something instead of falling on arbitrary 25s. */}
                            <YAxis
                              stroke="var(--youro-muted)"
                              domain={[0, 100]}
                              ticks={[0, 30, 60, 90, 100]}
                              tick={{ fontSize: 12 }}
                              tickLine={false}
                              width={44}
                            />
                            <Tooltip
                              trigger='hover'
                              contentStyle={{
                                borderRadius: '10px',
                                border: '1px solid var(--youro-border)',
                                backgroundColor: 'var(--card)',
                                fontSize: '0.8rem',
                              }}
                              labelFormatter={unix => moment(new Date(unix)).format('MMM D, YYYY')}
                              labelStyle={{ color: 'var(--youro-charcoal)', fontWeight: 600 }}
                              formatter={(value) => [`${value} / 100 — ${getSeverityLabel(value)}`, 'Score']}
                              itemStyle={{ color: 'var(--youro-charcoal)' }}
                            />
                            <Line
                              type="monotone"
                              dataKey="symptomScore"
                              name="Score"
                              stroke="var(--youro-sage)"
                              cursor="pointer"
                              strokeWidth={2.5}
                              dot={{ r: 4, fill: 'var(--youro-sage)', strokeWidth: 0 }}
                              activeDot={{ r: 6 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                        </div>
                      </>
                  }
                  {
                      props.selectedOption != '' && props.selectedScore.length == 0 && <>
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="prev-symp-helper">
                          <h3 style={{ display: 'inline-block', fontStyle: 'italic', padding: '3%' }}><i>No Record of Symptom Score for this diagnosis</i></h3>
                        </div>
                      </>
                  }
                </div>
              )}
            </div>

            {/* Footer Section */}
            <div style={{ marginTop: '4px' }}>
              <div className="symptom-assessment-footer" style={{ width: '70%', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <button
                  className="symptom-assessment-cta"
                  disabled={!canTakeAssessment}
                  onClick={() => props.onFillSymptomClick(
                    nextEligibleAppt.diagName,
                    nextEligibleAppt.diagId,
                    nextEligibleAppt.apptId
                  )}
                  style={{
                    width: '100%',
                    backgroundColor: canTakeAssessment ? 'var(--youro-coral)' : 'var(--youro-stone)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: canTakeAssessment ? 'pointer' : 'not-allowed',
                    opacity: canTakeAssessment ? 1 : 0.7,
                    transition: 'all 0.2s'
                  }}
                >
                  {alreadyAssessedForAppt
                    ? 'Assessment Completed'
                    : 'Take Symptom Assessment'}
                </button>
              </div>
              {!canTakeAssessment && (
                <p style={{ margin: '8px 0 0 0', fontSize: '0.75rem', opacity: '0.7', textAlign: 'center' }}>
                  {alreadyAssessedForAppt
                    ? "You've completed the assessment for your next appointment. Your provider will review it with you."
                    : 'Assessments can be taken within 1 week of your next scheduled appointment'}
                </p>
              )}
            </div>
          </div>
        </>
      }

      <Popup
        open={showSeverity}
        modal
        closeOnDocumentClick={false}
        onClose={() => {
          setShowSeverity(false);
        }}
        className="symptom-popup"
      >
        <div
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            cursor: "pointer",
            zIndex: 2,
            backgroundColor: "#ffffff",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => {
            setShowSeverity(false);
          }}
        >
          <span className="material-symbols-outlined">close</span>
        </div>

        <div style={{ padding: "30px 40px" }}>
          <h2 style={{ margin: "0 0 4px", textAlign: "center" }}>Score Breakdown</h2>
          <p style={{ margin: "0 0 20px", textAlign: "center", opacity: 0.7, fontSize: "0.95rem" }}>
            What your symptom score means
          </p>
          {SEVERITY_TIERS.map((tier) => {
            const isCurrentTier = getSeverityTier(props.score) === tier;
            return (
              <div
                key={tier.label}
                style={{
                  backgroundColor: tier.bgColor,
                  border: `1px solid ${tier.borderColor}`,
                  borderRadius: "10px",
                  padding: "14px 16px",
                  marginBottom: "12px",
                  boxShadow: isCurrentTier ? `0 0 0 2px ${tier.textColor}` : "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <strong style={{ color: tier.textColor }}>
                    {tier.label} ({tier.min}-{tier.max})
                  </strong>
                  {isCurrentTier && (
                    <span style={{ fontSize: "0.75rem", fontWeight: 600, color: tier.textColor }}>
                      Your current score
                    </span>
                  )}
                </div>
                <p style={{ margin: "6px 0 0", fontSize: "0.9rem", color: "#333" }}>{tier.description}</p>
              </div>
            );
          })}
        </div>
      </Popup>
    </div >
  );

};

export default PatientSymptomChart;
